/** @module The collection as tiles or as a table (a list on phones), per copy or per variant. */
import { ArrowDownRight, ArrowUpRight, Check, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { isFullArt, printedNumber, variantLabel } from "@/lib/cards";
import { money, signedMoney } from "@/lib/format";

export type Item = components["schemas"]["CollectionItemDetailOut"];
type Variant = components["schemas"]["CollectionVariantOut"];
type Price = components["schemas"]["ItemPriceOut"];

/** One line of the list: a single copy, or all copies of a variant ("3×"). */
export interface Row {
  key: string;
  /** The copies behind the row; selecting the row selects all of them. */
  ids: string[];
  printId: string;
  card: Item["card"];
  quantity: number;
  languages: string[];
  conditions: string[];
  tags: { id: string; name: string }[];
  value: string | null;
  price: Price | null;
  /** What the copies cost, as far as known. */
  cost: string | null;
  /** Value minus cost over the copies where both are known. */
  gain: string | null;
  /** Where the copies lie, as paths. */
  places: string[];
  /** Set for a single copy, which opens directly instead of on the card page. */
  item?: Item;
}

export function rowOfItem(item: Item): Row {
  return {
    key: item.id,
    ids: [item.id],
    printId: item.print_id,
    card: item.card,
    quantity: 1,
    languages: [item.language],
    conditions: [item.condition],
    tags: item.tags ?? [],
    value: item.value ?? null,
    price: item.price ?? null,
    cost: item.purchase_price ?? null,
    gain: item.gain ?? null,
    places: item.location ? [item.location.name] : [],
    item,
  };
}

export function rowOfVariant(variant: Variant): Row {
  return {
    key: `${variant.print_id}-${String(variant.variant_id)}`,
    ids: variant.item_ids,
    printId: variant.print_id,
    card: variant.card,
    quantity: variant.quantity,
    languages: variant.languages,
    conditions: variant.conditions,
    tags: variant.tags ?? [],
    value: variant.value ?? null,
    price: variant.price ?? null,
    cost: variant.invested ?? null,
    gain: variant.gain ?? null,
    places: (variant.locations ?? []).map((location) => location.name),
  };
}

/** A suspicious or missing price is named, never shown as 0,00 €. */
export function RowValue({ row }: { row: Row }) {
  const { t, i18n } = useTranslation();
  if (row.price?.suspicious) {
    return (
      <span className="badge badge-warn">
        <TriangleAlert />
        {t("collection.suspicious")}
      </span>
    );
  }
  const value = money(row.value, i18n.resolvedLanguage ?? "de");
  return value ? (
    <span className="num">{value}</span>
  ) : (
    <span className="t-xs t-muted">{t("collection.unvalued")}</span>
  );
}

/** Gain since purchase; a dash where the price paid or the value is unknown. */
export function RowGain({ row }: { row: Row }) {
  const { i18n } = useTranslation();
  if (row.gain === null) return <span className="t-muted">–</span>;
  return (
    <span className={Number(row.gain) >= 0 ? "gain" : "loss"}>
      {signedMoney(row.gain, i18n.resolvedLanguage ?? "de")}
    </span>
  );
}

/** Change over seven days; a dash where one end of the week has no price. */
export function RowChange({ row }: { row: Row }) {
  const { i18n } = useTranslation();
  const change = row.price?.change_7d;
  if (change === null || change === undefined) return <span className="t-muted">–</span>;
  if (Math.abs(change) < 0.05) return <span className="t-muted">± 0 %</span>;
  const up = change > 0;
  const figure = Math.abs(change).toLocaleString(i18n.resolvedLanguage ?? "de", {
    maximumFractionDigits: 1,
  });
  return (
    <span className={`row justify-end gap-1 ${up ? "gain" : "loss"}`}>
      {up ? <ArrowUpRight className="size-4" /> : <ArrowDownRight className="size-4" />}
      {up ? "+" : "−"}
      {figure} %
    </span>
  );
}

function Mark({ on }: { on: boolean }) {
  return <span className={`check ${on ? "on" : ""}`}>{on ? <Check /> : null}</span>;
}

function Languages({ row }: { row: Row }) {
  return (
    <span className="row gap-1">
      {row.languages.map((language) => (
        <span key={language} className="lang">
          {language.toUpperCase()}
        </span>
      ))}
    </span>
  );
}

function useLabels() {
  const { t } = useTranslation();
  return (row: Row) => {
    const card = row.card;
    return {
      name: card?.name ?? row.printId,
      number: card ? printedNumber(card) : row.printId,
      variant: card ? variantLabel(card.variant_key, (key, fallback) => t(key, fallback)) : "–",
    };
  };
}

export function GridView({
  rows,
  selected,
  onPick,
}: {
  rows: Row[];
  /** Undefined outside the selection mode. */
  selected?: Set<string>;
  onPick: (row: Row) => void;
}) {
  const labels = useLabels();
  return (
    <ul className="grid grid-cols-3 gap-x-[10px] gap-y-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
      {rows.map((row) => {
        const { name } = labels(row);
        const on = selected?.has(row.key) ?? false;
        return (
          <li key={row.key}>
            <button
              type="button"
              className="tile w-full"
              aria-pressed={selected ? on : undefined}
              onClick={() => {
                onPick(row);
              }}
            >
              <span className="tile-media block">
                <span
                  className={`card-shape block ${isFullArt(row.card?.rarity_class) ? "foil" : ""} ${on ? "ring-2 ring-(--accent) ring-offset-2 ring-offset-(--bg)" : ""}`}
                >
                  <CardFace printId={row.printId} name={name} lazy />
                </span>
                {selected ? (
                  <span className="tile-select">
                    <Mark on={on} />
                  </span>
                ) : null}
                {row.quantity > 1 ? (
                  <span className="badge badge-qty tile-qty">{row.quantity}×</span>
                ) : null}
              </span>
              <span className="tile-name">{name}</span>
              <span className="tile-meta">
                <span className="lang">
                  {(row.languages[0] ?? "").toUpperCase()}
                  {row.languages.length > 1 ? ` +${String(row.languages.length - 1)}` : ""}
                </span>
                {/* Three tiles on a phone leave little room: the badge shortens, never spills. */}
                <span className="flex min-w-0 justify-end overflow-hidden [&_.badge]:max-w-full [&_.badge]:truncate">
                  <RowValue row={row} />
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function TableView({
  rows,
  grouped,
  selected,
  onPick,
  onToggleAll,
}: {
  rows: Row[];
  /** Per variant: a quantity column, conditions and languages as lists. */
  grouped: boolean;
  selected?: Set<string>;
  onPick: (row: Row) => void;
  onToggleAll: () => void;
}) {
  const { t, i18n } = useTranslation();
  const labels = useLabels();
  const all = selected !== undefined && rows.length > 0 && selected.size === rows.length;
  return (
    <>
      {/* Phone: a list row per entry, the facts folded into one grey line. */}
      <ul className="flex flex-col md:hidden">
        {rows.map((row) => {
          const { name, number, variant } = labels(row);
          return (
            <li key={row.key}>
              <button
                type="button"
                aria-pressed={selected ? selected.has(row.key) : undefined}
                onClick={() => {
                  onPick(row);
                }}
                className="flex min-h-14 w-full items-center gap-[10px] border-b border-(--border) py-2 text-left"
              >
                {selected ? <Mark on={selected.has(row.key)} /> : null}
                <span className="thumb">
                  <CardFace printId={row.printId} name={name} lazy />
                </span>
                <span className="flex min-w-0 grow flex-col">
                  <b className="t-sm truncate">
                    {row.quantity > 1 ? `${String(row.quantity)}× ` : ""}
                    {name}
                  </b>
                  <span className="t-xs t-muted num truncate">
                    {number} · {variant} · {row.conditions.join(", ")}
                    {row.places.length > 0 ? ` · ${row.places.join(", ")}` : ""}
                  </span>
                </span>
                <Languages row={row} />
                <span className="t-sm">
                  <RowValue row={row} />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {/* Wide screens: the design's table, every fact in its own column. */}
      <div className="panel hidden overflow-x-auto md:block">
        <table className="table">
          <thead>
            <tr>
              {selected ? (
                <th className="w-11">
                  <button
                    type="button"
                    aria-label={t("collection.selectAllShort")}
                    onClick={onToggleAll}
                    className="grid"
                  >
                    <Mark on={all} />
                  </button>
                </th>
              ) : null}
              <th>{t("collection.card")}</th>
              <th>{t("collection.set")}</th>
              <th>{t("collection.variant")}</th>
              <th>{t("collection.language")}</th>
              <th>{t("collection.condition")}</th>
              <th>{t("collection.tags")}</th>
              <th>{t("locations.label")}</th>
              {grouped ? <th className="td-num">{t("collection.quantity")}</th> : null}
              <th className="td-num">{t("collection.value")}</th>
              <th className="td-num">{t("purchase.table.cost")}</th>
              <th className="td-num">{t("purchase.table.gain")}</th>
              <th className="td-num">{t("collection.change7d")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const { name, number, variant } = labels(row);
              const on = selected?.has(row.key);
              return (
                <tr
                  key={row.key}
                  aria-selected={on}
                  onClick={() => {
                    onPick(row);
                  }}
                  className="cursor-pointer"
                >
                  {selected ? (
                    <td>
                      <Mark on={on ?? false} />
                    </td>
                  ) : null}
                  <td>
                    {/* The row is clickable; this button is the keyboard's way in. */}
                    <button
                      type="button"
                      className="row gap-[10px] text-left"
                      aria-pressed={on}
                      onClick={(event) => {
                        event.stopPropagation();
                        onPick(row);
                      }}
                    >
                      <span className="thumb">
                        <CardFace printId={row.printId} name={name} lazy />
                      </span>
                      <b>{name}</b>
                    </button>
                  </td>
                  <td className="num whitespace-nowrap">{number}</td>
                  <td>{variant}</td>
                  <td>
                    <Languages row={row} />
                  </td>
                  <td className="num">{row.conditions.join(", ")}</td>
                  <td>
                    <span className="flex flex-wrap gap-1">
                      {row.tags.map((tag) => (
                        <span key={tag.id} className="badge badge-neutral">
                          {tag.name}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className="t-sm">
                    {row.places.length > 0 ? (
                      row.places.join(", ")
                    ) : (
                      <span className="t-muted">–</span>
                    )}
                  </td>
                  {grouped ? <td className="td-num">{row.quantity}</td> : null}
                  <td className="td-num">
                    <RowValue row={row} />
                  </td>
                  <td className="td-num num">
                    {money(row.cost, i18n.resolvedLanguage ?? "de") ?? (
                      <span className="t-muted">–</span>
                    )}
                  </td>
                  <td className="td-num num">
                    <RowGain row={row} />
                  </td>
                  <td className="td-num whitespace-nowrap">
                    <RowChange row={row} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
